import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

export const pagePaperSx = {
  elevation: 0,
  variant: 'outlined',
  sx: {
    borderRadius: 4,
    bgcolor: 'background.paper',
    boxShadow: '0 4px 24px rgba(42,147,143,0.06)',
    borderColor: 'divider',
    overflow: 'hidden',
  },
};

export const primaryButtonSx = {
  py: 1.5,
  borderRadius: 2.5,
  fontSize: '1rem',
  fontWeight: 800,
  background: 'linear-gradient(90deg, #3ab5b0, #2a938f)',
  boxShadow: '0 6px 18px rgba(42,147,143,0.28)',
  '&:hover': {
    background: 'linear-gradient(90deg, #2a938f, #1f6e6b)',
  },
};

export const outlinedButtonSx = {
  py: 1.5,
  borderRadius: 2.5,
  fontWeight: 700,
  borderColor: 'rgba(42,147,143,0.35)',
  color: 'primary.main',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'rgba(42,147,143,0.06)',
  },
};

export const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    bgcolor: 'background.paper',
  },
};

export const stepperSx = {
  px: { xs: 1, sm: 2 },
  pt: 3,
  pb: 1,
  '& .MuiStepLabel-label': {
    fontSize: { xs: 11, sm: 13 },
    fontWeight: 700,
  },
  '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
  '& .MuiStepIcon-root.Mui-completed': { color: 'primary.main' },
  '& .MuiStepConnector-line': { borderColor: 'rgba(42,147,143,0.2)' },
};

function PageTitle({ title, subtitle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, md: 1.5 } }}>
      <Box
        sx={{
          width: 5,
          height: { xs: 28, md: 34 },
          borderRadius: 999,
          flexShrink: 0,
          background: 'linear-gradient(180deg, #3ab5b0, #2a938f)',
          boxShadow: '0 2px 10px rgba(42,147,143,0.28)',
        }}
      />
      <Box>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            color: 'primary.main',
            fontSize: { xs: '1.45rem', md: '1.75rem' },
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: '0.95rem' }}>
            {subtitle}
          </Typography>
        ) : null}
        <Box
          sx={{
            mt: 0.75,
            width: { xs: 40, md: 52 },
            height: 3,
            borderRadius: 999,
            background: 'linear-gradient(90deg, #3ab5b0, #2a938f)',
            opacity: 0.85,
          }}
        />
      </Box>
    </Box>
  );
}

export default function AuthPageLayout({ title, subtitle, children }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ bgcolor: 'background.default', pb: { xs: 4, md: 6 }, minHeight: '60vh' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: { xs: 2.5, md: 3 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="sm">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{
              mb: 1.5,
              '& .MuiBreadcrumbs-li': { fontSize: '0.9rem' },
              '& .MuiLink-root:hover': { color: 'primary.main' },
            }}
          >
            <Link component={RouterLink} to="/" underline="hover" color="inherit">
              {t('app_name')}
            </Link>
            <Typography color="text.primary" fontWeight={700}>
              {title}
            </Typography>
          </Breadcrumbs>
          <PageTitle title={title} subtitle={subtitle} />
        </Container>
      </Box>

      <Container maxWidth="sm">{children}</Container>
    </Box>
  );
}
